import type { ReactNode } from 'react'
import type { DateSource, EmploymentStatus, Severity } from '@/data/queries'
import { shortDate } from '@/lib/format'

export function Card({
  title,
  aside,
  flush = false,
  children,
}: {
  title?: string
  aside?: ReactNode
  flush?: boolean
  children: ReactNode
}) {
  return (
    <div className="card">
      {title !== undefined && (
        <div className="head">
          <h2>{title}</h2>
          <div className="spacer" />
          {aside}
        </div>
      )}
      <div className={flush ? 'body flush' : 'body'}>{children}</div>
    </div>
  )
}

export function SeverityPill({ severity }: { severity: Severity }) {
  const label = severity === 'due_soon' ? 'Due soon' : severity[0]!.toUpperCase() + severity.slice(1)
  return <span className={`sev ${severity}`}>{label}</span>
}

const STATUS_LABEL: Record<EmploymentStatus, string> = {
  active: 'Active',
  pending: 'Starts soon',
  left: 'Terminated',
}

export function StatusPill({ status }: { status: EmploymentStatus }) {
  return <span className={`status ${status}`}>{STATUS_LABEL[status]}</span>
}

const PROVENANCE_TITLE: Record<DateSource, string> = {
  integration: 'Received from Planday',
  defaulted: 'Defaulted, not received. Needs confirming.',
  hr_confirmed: 'Confirmed by HR. Sync will not move it.',
}

/** A guessed date must never be able to pass for a known one. */
export function ProvenanceDate({ iso, source }: { iso: string | null; source: DateSource | null }) {
  if (iso === null) return <span className="mono">—</span>
  const kind = source ?? 'defaulted'
  return (
    <span className={`prov ${kind} mono`} title={PROVENANCE_TITLE[kind]}>
      {shortDate(iso)}
    </span>
  )
}

export function CompositionBar({
  parts,
  label,
}: {
  parts: { width: number; colour: string; label: string }[]
  label: string
}) {
  const total = parts.reduce((sum, p) => sum + p.width, 0) || 1
  return (
    <>
      <div className="meter" role="img" aria-label={label}>
        {parts.map((p, i) => (
          <i key={i} style={{ width: `${(p.width / total) * 100}%`, background: p.colour }} />
        ))}
      </div>
      <div className="meterkey">
        {parts
          .filter((p) => p.width > 0)
          .map((p, i) => (
            <span key={i}>
              <s style={{ background: p.colour }} />
              {p.width} {p.label}
            </span>
          ))}
      </div>
    </>
  )
}

export function Tile({
  label,
  value,
  children,
}: {
  label: string
  value: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="tile">
      <div className="eyebrow">{label}</div>
      <div className="val">{value}</div>
      {children}
    </div>
  )
}

export function Loading({ what }: { what: string }) {
  return (
    <div className="empty">
      <p>Loading {what}…</p>
    </div>
  )
}

export function Failed({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="empty">
      <h2>That did not load</h2>
      <p>{message}</p>
      {onRetry !== undefined && (
        <button className="btn quiet sm" type="button" onClick={onRetry} style={{ marginTop: 16 }}>
          Try again
        </button>
      )}
    </div>
  )
}

export function NotBuilt({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card">
      <div className="body">
        <div className="empty">
          <h2>{title}</h2>
          <p>{children}</p>
        </div>
      </div>
    </div>
  )
}
